import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

export default function Contact() {
  const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate textarea
    const message = formData.get("message") as string;
    if (message && message.length > 500) {
      return;
    }

    setSubmitStatus("loading");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/d6ttfh1qcrg2vc5aptq0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      if (response.ok) {
        setSubmitStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    }
  };

  return (
    <main>
      <Navbar />

      <section className="min-h-screen flex flex-col lg:flex-row pt-20">
        {/* Left: Company info */}
        <div className="bg-brand-noir lg:w-5/12 flex flex-col justify-center px-10 lg:px-16 py-24 lg:py-32">
          <span className="font-sans text-xs tracking-widest uppercase text-brand-gris-light mb-8">
            Studio
          </span>
          <h1 className="font-serif font-light text-brand-or-light text-3xl lg:text-4xl mb-10 leading-snug">
            MARCINSKI
          </h1>

          <div className="space-y-8 font-sans font-light text-sm text-white/60 leading-loose">
            <div>
              <p className="text-white/30 text-xs tracking-widest uppercase mb-2">Dirigeant</p>
              <p className="text-white/80">Oskar Arkadiusz Marcinski</p>
            </div>
            <div>
              <p className="text-white/30 text-xs tracking-widest uppercase mb-2">SIRET</p>
              <p className="text-white/80 font-mono">101 116 994 00019</p>
            </div>
            <div>
              <p className="text-white/30 text-xs tracking-widest uppercase mb-2">Adresse</p>
              <p className="text-white/80">37 Rue Lecocq</p>
              <p className="text-white/80">33000 Bordeaux</p>
              <p className="text-white/80">France</p>
            </div>
            <div>
              <p className="text-white/30 text-xs tracking-widest uppercase mb-2">Email</p>
              <a href="mailto:oskarmarcinsk1@inbox.eu" className="text-brand-or hover:text-white transition-colors duration-300 cursor-pointer">
                oskarmarcinsk1@inbox.eu
              </a>
            </div>
            <div>
              <p className="text-white/30 text-xs tracking-widest uppercase mb-2">Téléphone</p>
              <a href="tel:+33758860574" className="text-brand-or hover:text-white transition-colors duration-300 cursor-pointer">
                +33 7 58 86 05 74
              </a>
            </div>
            <div>
              <p className="text-white/30 text-xs tracking-widest uppercase mb-2">Activité</p>
              <p className="text-white/80">74.10Z – Activités spécialisées de design</p>
            </div>
          </div>

          {/* Decorative line */}
          <div className="mt-16 w-16 h-px bg-brand-or/40"></div>
        </div>

        {/* Right: Form */}
        <div className="bg-white lg:w-7/12 flex flex-col justify-center px-8 lg:px-16 xl:px-24 py-24 lg:py-32">
          <span className="font-sans text-xs tracking-widest uppercase text-brand-gris-light mb-6">
            Votre projet
          </span>
          <h2 className="font-serif font-light text-brand-noir text-4xl lg:text-5xl mb-12 leading-tight">
            Contact
          </h2>

          <p className="font-sans font-light text-brand-gris text-sm leading-loose mb-12 max-w-md">
            Vous avez un projet ou souhaitez échanger sur une collaboration ? Nous vous invitons
            à nous contacter.
          </p>

          {/* Success message */}
          {submitStatus === "success" && (
            <div className="mb-8 p-5 border-l-2 border-brand-or bg-brand-beige">
              <p className="font-sans text-sm text-brand-noir">
                Votre message a été envoyé avec succès. Nous vous répondrons dans les meilleurs délais.
              </p>
            </div>
          )}

          {/* Error message */}
          {submitStatus === "error" && (
            <div className="mb-8 p-5 border-l-2 border-red-400 bg-red-50">
              <p className="font-sans text-sm text-red-700">
                Une erreur est survenue. Veuillez réessayer.
              </p>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="space-y-10 max-w-lg"
          >
            {/* Nom */}
            <div className="group">
              <label className="block font-sans text-xs tracking-widest uppercase text-brand-gris-light mb-3">
                Nom <span className="text-brand-or">*</span>
              </label>
              <input
                type="text"
                name="nom"
                required
                placeholder="Votre nom"
                className="w-full border-b-2 border-gray-200 focus:border-brand-noir outline-none py-3 font-sans font-light text-brand-noir text-base bg-transparent placeholder:text-brand-gris-light/50 transition-colors duration-300 text-sm"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block font-sans text-xs tracking-widest uppercase text-brand-gris-light mb-3">
                Email <span className="text-brand-or">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="votre@email.com"
                className="w-full border-b-2 border-gray-200 focus:border-brand-noir outline-none py-3 font-sans font-light text-brand-noir text-base bg-transparent placeholder:text-brand-gris-light/50 transition-colors duration-300 text-sm"
              />
            </div>

            {/* Téléphone */}
            <div>
              <label className="block font-sans text-xs tracking-widest uppercase text-brand-gris-light mb-3">
                Téléphone
              </label>
              <input
                type="tel"
                name="telephone"
                placeholder="+33 6 00 00 00 00"
                className="w-full border-b-2 border-gray-200 focus:border-brand-noir outline-none py-3 font-sans font-light text-brand-noir text-base bg-transparent placeholder:text-brand-gris-light/50 transition-colors duration-300 text-sm"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block font-sans text-xs tracking-widest uppercase text-brand-gris-light mb-3">
                Description du projet <span className="text-brand-or">*</span>
              </label>
              <textarea
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="Décrivez votre projet, vos besoins et vos attentes..."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full border border-gray-200 focus:border-brand-noir outline-none p-4 font-sans font-light text-brand-noir text-sm bg-transparent placeholder:text-brand-gris-light/50 transition-colors duration-300 resize-none leading-loose"
              ></textarea>
              <div className="flex justify-end mt-1">
                <span className={`font-sans text-xs ${charCount > 480 ? "text-red-400" : "text-brand-gris-light"}`}>
                  {charCount}/500
                </span>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitStatus === "loading" || charCount > 500}
              className="w-full py-4 bg-brand-noir text-white font-sans text-sm tracking-widest uppercase hover:bg-brand-or transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitStatus === "loading" ? "Envoi en cours..." : "Envoyer"}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </main>
  );
}
